import { resolveWindowDayKeys } from '@/features/shared/utils/chartDateWindow';
import { getStatusCopy } from '@/shared/copy/status';
import { formatPercent } from '@/ui/formatters/formatPercent';
import { alpha, getChipPalette } from '@/ui/primitives';
import { resolveCoverageMetricThresholdColor } from './coverageMetricThresholdColors';
import type { CoverageRunSummaryModel } from './coverageRunsPageModel';
import type { MiniMetricChartSummary } from '@/features/shared/components/chartPanels/MiniMetricChartHeader';
import type { StatusPieSlice } from '@/ui';
import type { CoverageMetricThresholdGroup, CoverageRunDto } from '@flowqa/shared';

type CoverageMetricKey = 'lines' | 'functions' | 'branches' | 'statements';
type CoveragePieSliceKey = 'covered' | 'uncovered';

type CoverageMetricPoint = {
  covered: number | null;
  total: number | null;
  pct: number | null;
};

type CoverageSummaryMetric = {
  covered: number | null;
  total: number | null;
  pct: number | null;
};

export type CoverageMetricPanelModel = {
  id: CoverageMetricKey;
  title: string;
  latestPct: number | null;
  latestLabel: string;
  summary: MiniMetricChartSummary;
  accentColor: string;
  pieSlices: Array<StatusPieSlice<CoveragePieSliceKey>>;
  trendData: Array<{ label: string; covered: number; uncovered: number }>;
  lineTrendData: Array<{ label: string; measuredValue: number | null; carryForwardValue: number }>;
};

const COVERED_COLOR = alpha(getChipPalette('green').icon, 0.8);
const UNCOVERED_COLOR = alpha(getChipPalette('red').icon, 0.8);

const METRIC_COLORS: Record<CoverageMetricKey, string> = {
  lines: alpha(getChipPalette('teal').icon, 0.8),
  functions: alpha(getChipPalette('blue').icon, 0.8),
  branches: alpha(getChipPalette('yellow').icon, 0.8),
  statements: alpha(getChipPalette('purple').icon, 0.8),
};

const formatPercentLabel = (value: number | null): string =>
  value === null ? getStatusCopy('noData') : `${value.toFixed(1)}% latest`;

const resolveSummaryTone = (metric: CoverageMetricKey): MiniMetricChartSummary['valueTone'] => {
  switch (metric) {
    case 'lines':
      return 'teal';
    case 'functions':
      return 'blue';
    case 'branches':
      return 'orange';
    case 'statements':
      return 'green';
  }
};

const resolveThresholdSummaryTone = (
  metric: CoverageMetricKey,
  latestPct: number | null,
  thresholds: CoverageMetricThresholdGroup | null,
): MiniMetricChartSummary['valueTone'] => {
  if (thresholds === null || latestPct === null) {
    return resolveSummaryTone(metric);
  }

  const thresholdKey =
    metric === 'lines'
      ? 'line'
      : metric === 'functions'
        ? 'function'
        : metric === 'branches'
          ? 'branch'
          : 'statement';
  const thresholdColor = resolveCoverageMetricThresholdColor(latestPct, thresholds[thresholdKey]);

  if (thresholdColor === 'success') {
    return 'green';
  }
  if (thresholdColor === 'warning') {
    return 'orange';
  }
  if (thresholdColor === 'error') {
    return 'red';
  }

  return resolveSummaryTone(metric);
};

const resolveDeltaTone = (deltaValue: number | null): MiniMetricChartSummary['deltaTone'] => {
  if (deltaValue === null || deltaValue === 0) {
    return 'neutral';
  }

  return deltaValue > 0 ? 'positive' : 'negative';
};

const buildCoverageMetricSummary = (
  metric: CoverageMetricKey,
  latestPct: number | null,
  previousPct: number | null,
  thresholds: CoverageMetricThresholdGroup | null,
): MiniMetricChartSummary => {
  const deltaValue = latestPct === null || previousPct === null ? null : latestPct - previousPct;

  return {
    valueLabel: latestPct === null ? getStatusCopy('noData') : `${formatPercent(latestPct)}%`,
    valueTone: resolveThresholdSummaryTone(metric, latestPct, thresholds),
    deltaLabel:
      deltaValue === null
        ? null
        : deltaValue === 0
          ? getStatusCopy('noChange')
          : `${deltaValue > 0 ? '+' : ''}${formatPercent(deltaValue)}%`,
    deltaValue,
    deltaTone: resolveDeltaTone(deltaValue),
    isMissing: latestPct === null,
  };
};

const getSummaryMetric = (
  summary: CoverageRunSummaryModel | null | undefined,
  metric: CoverageMetricKey,
): CoverageSummaryMetric => {
  if (summary === null || summary === undefined) {
    return { covered: null, total: null, pct: null };
  }

  switch (metric) {
    case 'lines':
      return {
        covered: summary.linesCovered,
        total: summary.linesTotal,
        pct: summary.linesPct,
      };
    case 'functions':
      return {
        covered: summary.functionsCovered,
        total: summary.functionsTotal,
        pct: summary.functionsPct,
      };
    case 'branches':
      return {
        covered: summary.branchesCovered,
        total: summary.branchesTotal,
        pct: summary.branchesPct,
      };
    case 'statements':
      return {
        covered: summary.statementsCovered,
        total: summary.statementsTotal,
        pct: summary.statementsPct,
      };
  }
};

const hasSummaryMetricData = (metric: CoverageSummaryMetric): boolean =>
  metric.covered !== null && metric.total !== null && metric.total > 0 && metric.pct !== null;

const buildLabel = (run: CoverageRunDto, index: number): string =>
  run.commitSha?.slice(0, 8) ?? `Run ${index + 1}`;

const buildCapturedAtLabel = (run: CoverageRunDto, fallbackIndex: number): string =>
  run.capturedAt instanceof Date
    ? run.capturedAt.toISOString().slice(0, 10)
    : run.createdAt instanceof Date
      ? run.createdAt.toISOString().slice(0, 10)
      : `Run ${fallbackIndex + 1}`;

const resolveRunDayKey = (run: CoverageRunDto | null | undefined): string | null => {
  if (run?.capturedAt instanceof Date) {
    return run.capturedAt.toISOString().slice(0, 10);
  }
  if (run?.createdAt instanceof Date) {
    return run.createdAt.toISOString().slice(0, 10);
  }
  return null;
};

const getMetricPoint = (run: CoverageRunDto, metric: CoverageMetricKey): CoverageMetricPoint => {
  switch (metric) {
    case 'lines':
      return {
        covered: run.linesCovered,
        total: run.linesTotal,
        pct: run.linesPct,
      };
    case 'functions':
      return {
        covered: run.functionsCovered ?? null,
        total: run.functionsTotal ?? null,
        pct: run.functionsPct ?? null,
      };
    case 'branches':
      return {
        covered: run.branchesCovered ?? null,
        total: run.branchesTotal ?? null,
        pct: run.branchesPct ?? null,
      };
    case 'statements':
      return {
        covered: run.statementsCovered ?? null,
        total: run.statementsTotal ?? null,
        pct: run.statementsPct ?? null,
      };
  }
};

const buildPieSlices = (point: CoverageMetricPoint): Array<StatusPieSlice<CoveragePieSliceKey>> => {
  if (point.covered === null || point.total === null || point.total <= 0) {
    return [
      { key: 'covered', label: 'Covered', value: 0, color: COVERED_COLOR },
      { key: 'uncovered', label: 'Uncovered', value: 0, color: UNCOVERED_COLOR },
    ];
  }
  return [
    { key: 'covered', label: 'Covered', value: point.covered, color: COVERED_COLOR },
    {
      key: 'uncovered',
      label: 'Uncovered',
      value: Math.max(point.total - point.covered, 0),
      color: UNCOVERED_COLOR,
    },
  ];
};

const buildTrendData = (
  runs: CoverageRunDto[],
  metric: CoverageMetricKey,
): Array<{ label: string; covered: number; uncovered: number }> =>
  [...runs]
    .reverse()
    .map((run, index) => {
      const point = getMetricPoint(run, metric);
      if (point.pct === null) {
        return null;
      }
      const covered = Math.max(0, Math.min(100, Math.round(point.pct)));
      return {
        label: buildLabel(run, index),
        covered,
        uncovered: Math.max(100 - covered, 0),
      };
    })
    .filter(
      (point): point is { label: string; covered: number; uncovered: number } => point !== null,
    );

const buildLineTrendData = (
  runs: CoverageRunDto[],
  metric: CoverageMetricKey,
  chartDates: string[],
): Array<{ label: string; measuredValue: number | null; carryForwardValue: number }> =>
  buildTrendDataByDate(runs, metric, chartDates).map(({ label, pct, isMeasured }) => ({
    label,
    measuredValue: isMeasured ? pct : null,
    carryForwardValue: pct,
  }));

const buildTrendDataByDate = (
  runs: CoverageRunDto[],
  metric: CoverageMetricKey,
  chartDates: string[],
): Array<{ label: string; pct: number; isMeasured: boolean }> => {
  const totalsByDate = new Map<string, { covered: number; total: number }>();

  [...runs]
    .reverse()
    .map((run, index) => {
      const point = getMetricPoint(run, metric);
      const dateLabel = buildCapturedAtLabel(run, index);
      return point.covered === null || point.total === null || point.total <= 0
        ? null
        : { label: dateLabel, covered: point.covered, total: point.total };
    })
    .filter((point): point is { label: string; covered: number; total: number } => point !== null)
    .forEach((point) => {
      const current = totalsByDate.get(point.label) ?? { covered: 0, total: 0 };
      totalsByDate.set(point.label, {
        covered: current.covered + point.covered,
        total: current.total + point.total,
      });
    });

  const resolvedChartDates =
    chartDates.length > 0
      ? chartDates
      : [...totalsByDate.keys()].sort((left, right) => left.localeCompare(right));

  let lastKnownPct: number | null = null;

  return resolvedChartDates
    .map((label) => {
      const measuredTotals = totalsByDate.get(label);
      const measuredPct =
        measuredTotals && measuredTotals.total > 0
          ? (measuredTotals.covered / measuredTotals.total) * 100
          : undefined;
      const pct = measuredPct ?? lastKnownPct;
      if (pct === null) {
        return null;
      }
      lastKnownPct = pct;
      return { label, pct, isMeasured: measuredPct !== undefined };
    })
    .filter(
      (point): point is { label: string; pct: number; isMeasured: boolean } => point !== null,
    );
};

type BuildCoverageMetricPanelsArgs = {
  runs: CoverageRunDto[];
  summary?: CoverageRunSummaryModel | null;
  previousSummary?: CoverageRunSummaryModel | null;
  thresholds?: CoverageMetricThresholdGroup | null;
  startDay?: string | null;
  endDay?: string | null;
};

export const buildCoverageMetricPanels = ({
  runs,
  summary = null,
  previousSummary = null,
  thresholds = null,
  startDay = null,
  endDay = null,
}: BuildCoverageMetricPanelsArgs): CoverageMetricPanelModel[] => {
  const latestRun = runs.length > 0 ? runs[0] : null;
  const fallbackStartDay = resolveRunDayKey(runs[runs.length - 1]);
  const fallbackEndDay = resolveRunDayKey(runs[0]);
  const chartDates = resolveWindowDayKeys({
    startDay,
    endDay,
    fallbackStartDay,
    fallbackEndDay,
  });
  const metrics: Array<{ id: CoverageMetricKey; title: string }> = [
    { id: 'lines', title: 'Line Coverage' },
    { id: 'functions', title: 'Function Coverage' },
    { id: 'branches', title: 'Branch Coverage' },
    { id: 'statements', title: 'Statement Coverage' },
  ];

  return metrics.map((metric) => {
    const latestPoint = latestRun
      ? getMetricPoint(latestRun, metric.id)
      : { covered: null, total: null, pct: null };
    const summaryMetric = getSummaryMetric(summary, metric.id);
    const previousSummaryMetric = getSummaryMetric(previousSummary, metric.id);
    const summaryPct = hasSummaryMetricData(summaryMetric) ? summaryMetric.pct : null;
    const previousSummaryPct = hasSummaryMetricData(previousSummaryMetric)
      ? previousSummaryMetric.pct
      : null;
    return {
      id: metric.id,
      title: metric.title,
      latestPct: latestPoint.pct,
      latestLabel: formatPercentLabel(latestPoint.pct),
      summary: buildCoverageMetricSummary(metric.id, summaryPct, previousSummaryPct, thresholds),
      accentColor: METRIC_COLORS[metric.id],
      pieSlices: buildPieSlices(latestPoint),
      trendData: buildTrendData(runs, metric.id),
      lineTrendData: buildLineTrendData(runs, metric.id, chartDates),
    };
  });
};
